import {PLANNER_PATIENT_VIEW_MODES} from './plannerPatientBrowser';

export const PLANNER_COMPLETED_FILTERS = Object.freeze({
  all: 'all',
  unlinked: 'unlinked',
  removed: 'removed',
  phone: 'phone',
});

const sectionStageFallbacks: Record<string, string> = {
  completed: 'completed',
  incomplete: 'active',
  noShows: 'noShow',
  upcoming: 'upcoming',
};

const scheduledStages = new Set(['upcoming']);
const completedStages = new Set(['completed', 'noShow']);

export const plannerBrowserEventStage = (event: any, section: any = null) =>
  event?.stage || sectionStageFallbacks[section?.key] || '';

export const isPlannerCompletedEvent = (event: any) =>
  completedStages.has(event?.stage);

const getEventsByStage = (sections: any[] = [], stageSet: Set<string>) =>
  sections.flatMap(section =>
    (section.events || []).filter((event: any) =>
      stageSet.has(plannerBrowserEventStage(event, section)),
    ),
  );

export const getPlannerScheduleEvents = (sections: any[] = []) =>
  getEventsByStage(sections, scheduledStages).filter(
    (event: any) => event?.source === 'appointment',
  );

export const getPlannerSearchScheduledEvents = (sections: any[] = []) =>
  getEventsByStage(sections, scheduledStages);

export const getPlannerCompletedEvents = (sections: any[] = []) =>
  getEventsByStage(sections, completedStages);

export const getPlannerCompletedUnlinkedEvents = (events: any[] = []) =>
  events.filter(event => !event?.patient && event?.source !== 'phoneCall');

const sourceIdKey = (event: any) => {
  const key = String(event?.sourceId ?? '').trim();
  return key || null;
};

export const mergePlannerEventsBySourceId = (
  primaryEvents: any[] = [],
  secondaryEvents: any[] = [],
) => {
  const seen = new Set();
  const merged: any[] = [];

  primaryEvents.forEach(event => {
    const key = sourceIdKey(event);
    if (key) seen.add(key);
    merged.push(event);
  });

  secondaryEvents.forEach(event => {
    const key = sourceIdKey(event);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    merged.push(event);
  });

  return merged;
};

export const buildPlannerBrowserEventTabs = ({
  sections = [],
  historyEvents = [],
  phoneEvents = [],
}: Record<string, any> = {}) => ({
  [PLANNER_PATIENT_VIEW_MODES.schedule]: getPlannerScheduleEvents(sections),
  [PLANNER_PATIENT_VIEW_MODES.completed]: mergePlannerEventsBySourceId(
    getPlannerCompletedEvents(sections),
    historyEvents.filter(isPlannerCompletedEvent),
  ),
  [PLANNER_PATIENT_VIEW_MODES.phone]: phoneEvents,
});

export const buildCompletedFilterEvents = ({
  completedEvents = [],
  completedFilter = PLANNER_COMPLETED_FILTERS.all,
  completedUnlinkedEvents = [],
  phoneModeEvents = [],
}: Record<string, any> = {}) => {
  if (completedFilter === PLANNER_COMPLETED_FILTERS.unlinked)
    return completedUnlinkedEvents;
  if (completedFilter === PLANNER_COMPLETED_FILTERS.phone)
    return phoneModeEvents;
  return mergePlannerEventsBySourceId(completedEvents, phoneModeEvents);
};
